import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, DatePicker, Dropdown, Select, Spin, Table, Tooltip, message } from "antd";
import { ExportOutlined, SyncOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import HighchartsReact from "highcharts-react-official";
import { Link } from "react-router-dom";
import {
  AnalyticsPollCancelledError,
  DEFAULT_MAX_POLL_ATTEMPTS,
  getUsageDashboard,
  getUsageWidgetDetail,
  resyncAnalyticsMappings,
} from "../../../apis/analyticsUsageApi";
import AnalyticsCacheBuildingLoader from "./AnalyticsCacheBuildingLoader";
import {
  purgeLegacyDashboardCache,
  readDashboardPrefs,
  writeDashboardPrefs,
} from "../../../lib/analytics/analyticsDashboardCache";
import AnalyticsChart from "./AnalyticsChart";
import AnalyticsDetailModal from "./AnalyticsDetailModal";
import HotLeadsPanel from "./HotLeadsPanel";
import AnalyticsHelpIcon from "./AnalyticsHelpIcon";
import AnalyticsPanelHead, { AnalyticsSectionHead } from "./AnalyticsPanelHead";
import WorldUserMap from "./WorldUserMap";
import {
  buildInvestmentFunnelFromSummary,
  buildSignupFunnelFromAnalytics,
  filterSignupFunnelSteps,
} from "./funnelHelpers";
import {
  barChartOptions,
  dataQualityDonut,
  donutOptions,
  formatDuration,
  formatCurrency,
  formatNumber,
  formatPct,
  formatProductLabel,
  mergeProductRows,
  funnelChartOptions,
  Highcharts,
  lineChartOptions,
  retentionHeatmapOptions,
  sparklineOptions,
  THEME,
} from "./chartHelpers";
import { ANALYTICS_DATE_PICKER_CLASS } from "./analyticsTheme";
import {
  buildDashboardExportSheets,
  exportAllDashboardExcel,
  exportAllDashboardUsersExcel,
} from "./analyticsExport";
import WidgetExportButton, { AnalyticsExportContext } from "./WidgetExportButton";

const { RangePicker } = DatePicker;
const MAX_RANGE_DAYS = 90;

const KPI_DEFS = [
  { key: "dau", label: "DAU", helpKey: "kpi-dau" },
  { key: "wau", label: "WAU", helpKey: "kpi-wau" },
  { key: "mau", label: "MAU", helpKey: "kpi-mau" },
  { key: "page_views", label: "Page Views", helpKey: "kpi-page_views" },
  { key: "installs", label: "Installs", helpKey: "kpi-installs" },
  { key: "signups", label: "Signups", helpKey: "kpi-signups" },
  { key: "logins", label: "Logins", helpKey: "kpi-logins" },
  { key: "investments", label: "Investments", helpKey: "kpi-investments" },
  { key: "investment_amount", label: "Investment Amount", format: formatCurrency, helpKey: "kpi-investment_amount" },
];

const ENGAGEMENT_DEFS = [
  { key: "avg_session_duration_sec", label: "Avg Session Duration", format: formatDuration },
  { key: "screens_per_session", label: "Screens / Session" },
  { key: "bounce_rate", label: "Bounce Rate", format: (v) => formatPct(v) },
  { key: "feature_usage", label: "Feature Usage", sub: "Unique Features" },
  { key: "api_failure_rate", label: "API Failure Rate", format: (v) => formatPct(v) },
];

const defaultRange = () => {
  const prefs = readDashboardPrefs();
  if (prefs?.from && prefs?.to) {
    const from = dayjs(prefs.from);
    const to = dayjs(prefs.to);
    if (from.isValid() && to.isValid()) return [from.startOf("day"), to.endOf("day")];
  }
  return [dayjs().subtract(29, "day").startOf("day"), dayjs().endOf("day")];
};

const initialFilters = () => {
  const prefs = readDashboardPrefs();
  return {
    platform: prefs?.platform || "all",
    source: prefs?.source || "all",
    compareEnabled: prefs?.compareEnabled !== false,
  };
};

const ChangeBadge = ({ pct, invert = false }) => {
  const up = pct > 0;
  const flat = pct === 0;
  const good = invert ? !up : up;
  return (
    <span className={`analytics-change ${flat ? "flat" : good ? "up" : "down"}`}>
      <i className={`fa-solid fa-arrow-${flat ? "right" : up ? "up" : "down"}`} />
      {Math.abs(pct).toFixed(1)}%
    </span>
  );
};

const ViewMoreBtn = ({ onClick, label = "View more" }) => (
  <button
    type="button"
    className="analytics-view-more"
    onClick={onClick}
    aria-label={label}
  >
    <span>{label}</span>
    <i className="fa-solid fa-arrow-right" />
  </button>
);

const formatPlatformLabel = (label) => {
  const key = String(label || "").toLowerCase();
  if (key === "android") return "Android";
  if (key === "ios") return "iOS";
  if (!key || key === "unknown") return "Android";
  return label;
};

const Panel = ({
  title,
  subtitle,
  extra,
  children,
  className = "",
  detailKey,
  helpKey,
  onViewDetail,
  exportSheet,
  exportSlug,
  exportParams,
  widgetKey,
  hotLeadsRows,
  showExport = false,
}) => (
  <div
    className={`analytics-panel ${detailKey ? "analytics-panel--clickable" : ""} ${className}`}
    onClick={detailKey && onViewDetail ? () => onViewDetail(detailKey) : undefined}
    onKeyDown={
      detailKey && onViewDetail
        ? (e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              onViewDetail(detailKey);
            }
          }
        : undefined
    }
    role={detailKey ? "button" : undefined}
    tabIndex={detailKey ? 0 : undefined}
  >
    {(title || subtitle || extra || detailKey) && (
      <AnalyticsPanelHead
        title={title}
        subtitle={subtitle}
        helpKey={helpKey || detailKey}
        extra={
          <>
            {extra}
            {(exportSheet || showExport) && (
              <WidgetExportButton
                sheet={exportSheet}
                slug={exportSlug || widgetKey || detailKey}
                widgetKey={widgetKey || exportSlug || detailKey}
                exportParams={showExport ? exportParams : undefined}
                hotLeadsRows={hotLeadsRows}
              />
            )}
            {detailKey && onViewDetail && (
              <ViewMoreBtn
                onClick={(e) => {
                  e.stopPropagation();
                  onViewDetail(detailKey);
                }}
              />
            )}
          </>
        }
      />
    )}
    {children}
  </div>
);

const EventSparkline = ({ data }) => (
  <HighchartsReact
    highcharts={Highcharts}
    options={sparklineOptions(data || [0])}
    containerProps={{ style: { height: 32, width: 80 } }}
  />
);

const AnalyticsUsageDashboard = () => {
  const init = useMemo(() => initialFilters(), []);
  const [dateRange, setDateRange] = useState(defaultRange);
  const [detailKey, setDetailKey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cacheBuilding, setCacheBuilding] = useState(null);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [compareEnabled, setCompareEnabled] = useState(init.compareEnabled);
  const [platform, setPlatform] = useState(init.platform);
  const [source, setSource] = useState(init.source);
  const [trendMode, setTrendMode] = useState("daily");
  const [resyncing, setResyncing] = useState(false);
  const [resyncMessage, setResyncMessage] = useState(null);
  const [exportAllUsersLoading, setExportAllUsersLoading] = useState(false);
  const fetchGenRef = useRef(0);

  useEffect(() => {
    purgeLegacyDashboardCache();
  }, []);

  const params = useMemo(
    () => ({
      from: dateRange[0].format("YYYY-MM-DD"),
      to: dateRange[1].format("YYYY-MM-DD"),
      platform: platform !== "all" ? platform : undefined,
      source: source !== "all" ? source : undefined,
    }),
    [dateRange, platform, source]
  );

  useEffect(() => {
    writeDashboardPrefs({
      from: params.from,
      to: params.to,
      platform,
      source,
      compareEnabled,
    });
  }, [params.from, params.to, platform, source, compareEnabled]);

  const load = useCallback(async () => {
    const gen = fetchGenRef.current + 1;
    fetchGenRef.current = gen;
    setLoading(true);
    setError(null);
    setCacheBuilding(null);

    try {
      const res = await getUsageDashboard(params, {
        isCancelled: () => fetchGenRef.current !== gen,
        onBuilding: ({ meta, attempt, maxAttempts, retryAfterSeconds }) => {
          if (fetchGenRef.current !== gen) return;
          setCacheBuilding({
            message:
              meta?.message ||
              "Analytics dashboard is still loading from PostHog. Retry this request shortly.",
            attempt,
            maxAttempts,
            retryAfterSeconds,
            cacheStatus: meta?.cache || "building",
          });
        },
      });
      if (fetchGenRef.current !== gen) return;

      setData(res?.result || null);
      if (!res?.result) {
        setError("No analytics data returned for this period.");
      }
    } catch (e) {
      if (fetchGenRef.current !== gen) return;
      if (e instanceof AnalyticsPollCancelledError) return;
      setError(
        e?.response?.data?.message ||
          e?.message ||
          "Failed to load analytics. Check PostHog config on inv-api.",
      );
    } finally {
      if (fetchGenRef.current === gen) {
        setLoading(false);
        setCacheBuilding(null);
      }
    }
  }, [params]);

  useEffect(() => {
    load();
  }, [load]);

  const handleRefresh = () => load();

  const handleResync = async () => {
    setResyncing(true);
    setResyncMessage(null);
    try {
      const res = await resyncAnalyticsMappings({
        from: params.from,
        to: params.to,
        only_missing: true,
      });
      const summary = res?.result?.summary || {};
      setResyncMessage({
        type: "success",
        text: `Resync complete: ${summary.total_updated ?? 0} mapping(s) updated (AppsFlyer: ${summary.appsflyer_attribution_updated ?? 0}, PostHog attribution: ${summary.posthog_attribution_updated ?? 0}, PostHog IDs: ${summary.posthog_distinct_id_updated ?? 0}, ad clicks: ${summary.ad_clicks_matched ?? 0}).`,
      });
      await load();
    } catch (e) {
      setResyncMessage({
        type: "error",
        text:
          e?.response?.data?.message ||
          e?.message ||
          "Resync failed. Check inv-api logs and PostHog/AppsFlyer config.",
      });
    } finally {
      setResyncing(false);
    }
  };

  const openDetail = useCallback((key) => setDetailKey(key), []);
  const closeDetail = useCallback(() => setDetailKey(null), []);

  const compLabel = useMemo(() => {
    if (!data?.comparison_period || !compareEnabled) return "";
    const f = dayjs(data.comparison_period.from).format("MMM D");
    const t = dayjs(data.comparison_period.to).format("MMM D");
    return `vs ${f}–${t}`;
  }, [data, compareEnabled]);

  const activeTrendChart = useMemo(() => {
    const trend = data?.active_users_trend || {};
    const dau = trend.dau || [];
    const wau = trend.wau || [];
    const mau = trend.mau || [];

    if (trendMode === "weekly") {
      return lineChartOptions({
        categories: wau.map((w) => dayjs(w.date).format("MMM D")),
        yTitle: "Users",
        series: [{ name: "WAU", data: wau.map((w) => w.wau), color: THEME.success }],
      });
    }

    return lineChartOptions({
      categories: dau.map((d) => dayjs(d.date).format("MMM D")),
      yTitle: "Users",
      series: [
        { name: "DAU", data: dau.map((d) => d.dau), color: THEME.primary },
        {
          name: "MAU",
          data: mau.length
            ? dau.map((d, i) => mau[Math.min(i, mau.length - 1)]?.mau ?? 0)
            : dau.map(() => 0),
          color: THEME.purple,
          dashStyle: "ShortDot",
        },
      ],
    });
  }, [data, trendMode]);

  const installTrendChart = useMemo(() => {
    const trend = data?.installs_trend || {};
    const daily = trend.daily || [];
    const weekly = trend.weekly || [];

    if (trendMode === "weekly") {
      return lineChartOptions({
        categories: weekly.map((w) => dayjs(w.date).format("MMM D")),
        yTitle: "Installs",
        series: [{ name: "Installs", data: weekly.map((w) => w.installs), color: THEME.purple }],
      });
    }

    return lineChartOptions({
      categories: daily.map((d) => dayjs(d.date).format("MMM D")),
      yTitle: "Installs",
      series: [{ name: "Installs", data: daily.map((d) => d.installs), color: THEME.purple }],
    });
  }, [data, trendMode]);

  const hasInstallTrend =
    (data?.installs_trend?.daily || []).length > 0 ||
    (data?.installs_trend?.weekly || []).length > 0;

  const installTrendNote = useMemo(() => {
    const ev = data?.installs_meta?.source_event;
    if (!ev) return null;
    if (ev === "appsflyer_install_conversion_received") return "AppsFlyer install events";
    return "Attribution detected events (AppsFlyer install events not found in range)";
  }, [data]);

  const acquisitionSources = data?.acquisition?.sources || [];
  const acquisitionPlatforms = data?.acquisition?.platforms || [];

  const normalizedPlatforms = useMemo(() => {
    const map = new Map();
    acquisitionPlatforms.forEach((p) => {
      const label = formatPlatformLabel(p.label);
      map.set(label, (map.get(label) || 0) + (p.users || 0));
    });
    const total = Array.from(map.values()).reduce((sum, n) => sum + n, 0) || 1;
    return Array.from(map.entries()).map(([label, users]) => ({
      label,
      users,
      pct: users / total,
    }));
  }, [acquisitionPlatforms]);

  const normalizedSources = useMemo(() => {
    const total = acquisitionSources.reduce((s, x) => s + (x.users || x.count || 0), 0) || 1;
    return acquisitionSources.map((item) => ({
      label: item.label,
      users: item.users || item.count || 0,
      pct: (item.users || item.count || 0) / total,
    }));
  }, [acquisitionSources]);

  const acquisitionTotalUsers = useMemo(
    () => normalizedSources.reduce((s, x) => s + x.users, 0),
    [normalizedSources]
  );

  const sourcesDonut = useMemo(
    () => donutOptions(normalizedSources, "Sources"),
    [normalizedSources]
  );

  const platformDonut = useMemo(
    () => donutOptions(normalizedPlatforms, "Platform"),
    [normalizedPlatforms]
  );

  const installsBar = useMemo(() => {
    const items = data?.attributed_installs || [];
    return barChartOptions({
      categories: items.map((i) => i.channel),
      data: items.map((i) => i.count),
      color: THEME.purple,
    });
  }, [data]);

  const signupCompletionBar = useMemo(() => {
    const items = data?.signup_analytics?.step_completions || [];
    return barChartOptions({
      categories: items.map((i) => i.label),
      data: items.map((i) => i.count),
      color: THEME.primary,
    });
  }, [data]);

  const sourceOptions = useMemo(() => {
    const sources = data?.acquisition?.sources || [];
    return [
      { value: "all", label: "All Sources" },
      ...sources.map((s) => ({ value: s.label, label: s.label })),
    ];
  }, [data]);

  const funnelSteps = useMemo(() => {
    const fromApi = data?.investment_funnel?.funnel || [];
    if (fromApi.length) return fromApi;
    return buildInvestmentFunnelFromSummary(data);
  }, [data]);

  const signupFunnelSteps = useMemo(() => {
    const fromApi =
      data?.signup_funnel?.extended_funnel?.length > 0
        ? data.signup_funnel.extended_funnel
        : data?.signup_funnel?.funnel || [];
    const steps = fromApi.length ? fromApi : buildSignupFunnelFromAnalytics(data?.signup_analytics);
    return filterSignupFunnelSteps(steps);
  }, [data]);

  const leadToInvestorSteps = useMemo(
    () => data?.engagement_funnel?.funnel || [],
    [data]
  );

  const leadToInvestorChart = useMemo(
    () => funnelChartOptions(leadToInvestorSteps, { light: true }),
    [leadToInvestorSteps]
  );
  const authMetric = (key) =>
    (data?.auth_performance_table || []).find((row) => row.key === key)?.cur;
  const rawSignupSummary = data?.signup_funnel?.summary || data?.signup_analytics?.summary || {};
  const signupSummary = {
    ...rawSignupSummary,
    flow_started: rawSignupSummary.flow_started ?? authMetric("signup_flow_started") ?? 0,
    completed: rawSignupSummary.completed ?? authMetric("posthog_signups") ?? 0,
    abandoned: rawSignupSummary.abandoned ?? authMetric("signup_abandoned") ?? 0,
    failed: rawSignupSummary.failed ?? authMetric("signup_failed") ?? 0,
  };
  const signupStepCompletions = data?.signup_analytics?.step_completions || [];
  const hasActiveTrend =
    (data?.active_users_trend?.dau || []).length > 0 ||
    (data?.active_users_trend?.wau || []).length > 0;
  const eventsOverview = data?.events_overview || [];
  const dq = data?.data_quality || {};
  const engKpis = data?.engagement_kpis || {};
  const topProducts = useMemo(() => mergeProductRows(data?.top_products || []), [data?.top_products]);
  const campaignRoi = data?.campaign_roi || { rows: [], attribution_available: false };
  const reengagement = data?.reengagement || [];
  const hotLeads = data?.hot_leads || { rows: [], total: 0 };
  const geoSummary = data?.geo_summary || {};

  const exportSheets = useMemo(
    () =>
      buildDashboardExportSheets({
        data,
        KPI_DEFS,
        ENGAGEMENT_DEFS,
        trendMode,
        normalizedSources,
        normalizedPlatforms,
        signupFunnelSteps,
        funnelSteps,
        leadToInvestorSteps,
        signupSummary,
        signupStepCompletions,
        topProducts,
        compareEnabled,
        params,
      }),
    [
      data,
      trendMode,
      normalizedSources,
      normalizedPlatforms,
      signupFunnelSteps,
      funnelSteps,
      leadToInvestorSteps,
      signupSummary,
      signupStepCompletions,
      topProducts,
      compareEnabled,
      params,
    ]
  );

  const exportContextValue = useMemo(() => ({ params }), [params]);

  const handleExportAllUsers = useCallback(async () => {
    setExportAllUsersLoading(true);
    const hide = message.loading("Fetching user data for all widgets…", 0);
    try {
      const ok = await exportAllDashboardUsersExcel({
        params,
        hotLeadsRows: hotLeads.rows,
        exportSheets,
        fetchWidgetDetail: getUsageWidgetDetail,
      });
      hide();
      if (!ok) message.warning("No user data to export for this period.");
      else message.success("User export downloaded.");
    } catch (err) {
      hide();
      message.error(err?.message || "Failed to export all users");
    } finally {
      setExportAllUsersLoading(false);
    }
  }, [params, hotLeads.rows, exportSheets]);

  const funnelColumns = [
    { title: "Step", dataIndex: "step", key: "step" },
    { title: "Users", dataIndex: "count", key: "count", render: formatNumber },
    { title: "Conversion %", dataIndex: "conversion_rate", key: "conv", render: formatPct },
    {
      title: "Drop-off %",
      dataIndex: "dropoff_rate",
      key: "drop",
      render: (v) => <span className={v > 0.3 ? "text-danger-analytics" : ""}>{formatPct(v)}</span>,
    },
  ];

  const screenColumns = [
    { title: "Screen", dataIndex: "screen", key: "screen" },
    { title: "Views", dataIndex: "views", key: "views", render: formatNumber },
    { title: "Users", dataIndex: "users", key: "users", render: formatNumber },
    { title: "Avg Time", dataIndex: "avg_time_sec", key: "time", render: formatDuration },
  ];

  const eventColumns = [
    { title: "Category", dataIndex: "category", key: "category" },
    { title: "Events", dataIndex: "count", key: "count", width: 90, render: formatNumber },
    {
      title: "Trend",
      dataIndex: "sparkline",
      key: "spark",
      width: 100,
      render: (v) => <EventSparkline data={v} />,
    },
  ];

  const signupStepColumns = [
    { title: "Step", dataIndex: "label", key: "label" },
    { title: "Completions", dataIndex: "count", key: "count", render: formatNumber },
  ];

  const authColumns = [
    { title: "Metric", dataIndex: "metric", key: "metric" },
    { title: "Count", dataIndex: "cur", key: "cur", render: formatNumber },
    {
      title: "Change",
      dataIndex: "change_pct",
      key: "chg",
      render: (v) => (compareEnabled ? <ChangeBadge pct={v} /> : "—"),
    },
  ];

  const attrColumns = [
    { title: "Source", dataIndex: "label", key: "label" },
    { title: "Users", dataIndex: "count", key: "count", render: formatNumber },
    { title: "%", dataIndex: "pct", key: "pct", render: formatPct },
  ];

  const productColumns = [
    { title: "Product", dataIndex: "product", key: "product", ellipsis: true, render: formatProductLabel },
    { title: "Views", dataIndex: "views", key: "views", width: 70, render: formatNumber },
    { title: "CTA Clicks", dataIndex: "cta_clicks", key: "cta", width: 90, render: formatNumber },
    { title: "Investments", dataIndex: "investments", key: "inv", width: 90, render: formatNumber },
    { title: "Conv. Rate", dataIndex: "conv_rate", key: "conv", width: 90, render: formatPct },
  ];

  const roiColumns = [
    { title: "Source", dataIndex: "source", key: "source", ellipsis: true },
    { title: "Attributed Users", dataIndex: "attributed_users", key: "users", render: formatNumber },
    { title: "Signups", dataIndex: "signups", key: "signups", render: formatNumber },
    { title: "Investments", dataIndex: "investments", key: "inv", render: formatNumber },
    { title: "Conv. Rate", dataIndex: "conv_rate", key: "conv", render: formatPct },
  ];

  const reengagementColumns = [
    { title: "Cohort", dataIndex: "cohort", key: "cohort", ellipsis: true },
    { title: "Users", dataIndex: "users", key: "users", width: 80, render: formatNumber },
    { title: "Suggested action", dataIndex: "suggested_action", key: "action", ellipsis: true },
  ];

  const geoCountryColumns = [
    { title: "Country", dataIndex: "country", key: "country" },
    { title: "Users", dataIndex: "users", key: "users", render: formatNumber },
    { title: "Events", dataIndex: "events", key: "events", render: formatNumber },
  ];

  const errorColumns = [
    { title: "Error", dataIndex: "message", key: "message", ellipsis: true },
    { title: "Count", dataIndex: "count", key: "count", width: 70 },
    { title: "Users", dataIndex: "users", key: "users", width: 70 },
    {
      title: "Last Seen",
      dataIndex: "last_seen",
      key: "last",
      width: 110,
      render: (v) => (v ? dayjs(v).format("MMM D HH:mm") : "—"),
    },
  ];

  return (
    <AnalyticsExportContext.Provider value={exportContextValue}>
    <div className="analytics-dashboard">
          <main className="analytics-dashboard__main">
            <header className="analytics-dashboard__header">
              <div>
                <h1>App Analytics</h1>
                <p>
                  Comprehensive mobile app insights from PostHog &amp; AppsFlyer
                  {(data?.analytics_scope?.excluded_countries?.length > 0) && (
                    <span className="analytics-scope-note">
                      {" "}· Excludes {(data.analytics_scope.excluded_countries.join(", "))} users
                    </span>
                  )}
                </p>
                {data?.updated_at && (
                  <small className="analytics-updated">
                    Data as of: {dayjs(data.updated_at).format("D MMM YYYY, h:mm A")}
                  </small>
                )}
              </div>
              <div className="analytics-dashboard__controls">
                <RangePicker
                  popupClassName={ANALYTICS_DATE_PICKER_CLASS}
                  value={dateRange}
                  onChange={(dates) => {
                    if (!dates?.[0] || !dates?.[1]) return;
                    if (dates[1].diff(dates[0], "day") > MAX_RANGE_DAYS) {
                      setError(`Max range is ${MAX_RANGE_DAYS} days`);
                      return;
                    }
                    setDateRange(dates);
                  }}
                  allowClear={false}
                  disabledDate={(c) => c && c > dayjs().endOf("day")}
                />
                <Select
                  value={compareEnabled ? "prev" : "none"}
                  onChange={(v) => setCompareEnabled(v === "prev")}
                  options={[
                    { value: "prev", label: "Compare: Previous Period" },
                    { value: "none", label: "No comparison" },
                  ]}
                  style={{ width: 200 }}
                />
                <Select
                  value={platform}
                  onChange={setPlatform}
                  options={[
                    { value: "all", label: "All Platforms" },
                    { value: "ios", label: "iOS" },
                    { value: "android", label: "Android" },
                  ]}
                  style={{ width: 130 }}
                />
                <Select value={source} onChange={setSource} options={sourceOptions} style={{ width: 150 }} />
                <Button type="primary" onClick={handleRefresh} loading={loading}>
                  Refresh
                </Button>
                <Tooltip title="Resync mappings">
                  <Button
                    icon={<SyncOutlined spin={resyncing} />}
                    onClick={handleResync}
                    loading={resyncing}
                    aria-label="Resync mappings"
                  />
                </Tooltip>
                <Dropdown
                  disabled={!data || loading || exportAllUsersLoading}
                  menu={{
                    items: [
                      {
                        key: "xlsx-all",
                        label: "Export all widgets (Excel)",
                        icon: <ExportOutlined />,
                        onClick: () =>
                          exportAllDashboardExcel(exportSheets, exportSheets._meta?.periodLabel),
                      },
                      {
                        key: "xlsx-all-users",
                        label: exportAllUsersLoading ? "Exporting all users…" : "Export all users (Excel)",
                        icon: <ExportOutlined />,
                        disabled: exportAllUsersLoading,
                        onClick: handleExportAllUsers,
                      },
                    ],
                  }}
                  trigger={["click"]}
                >
                  <Button icon={<ExportOutlined />} disabled={!data || loading || exportAllUsersLoading}>
                    Export
                  </Button>
                </Dropdown>
              </div>
            </header>

            {resyncMessage && (
              <Alert
                type={resyncMessage.type}
                message={resyncMessage.text}
                closable
                onClose={() => setResyncMessage(null)}
                showIcon
              />
            )}

            {error && <Alert type="error" message={error} closable onClose={() => setError(null)} showIcon />}

            {(platform !== "all" || source !== "all") && (
              <Alert
                type="info"
                showIcon
                className="analytics-filter-notice"
                message={`Filters active: ${platform !== "all" ? platform : "all platforms"} · ${source !== "all" ? source : "all sources"}. Some widgets only show matching PostHog events.`}
              />
            )}

            {loading && !data ? (
              cacheBuilding ? (
                <AnalyticsCacheBuildingLoader
                  message={cacheBuilding.message}
                  attempt={cacheBuilding.attempt}
                  maxAttempts={cacheBuilding.maxAttempts || DEFAULT_MAX_POLL_ATTEMPTS}
                  retryAfterSeconds={cacheBuilding.retryAfterSeconds}
                  cacheStatus={cacheBuilding.cacheStatus}
                />
              ) : (
                <div className="analytics-dashboard__loading">
                  <Spin size="large" tip="Loading analytics..." />
                </div>
              )
            ) : data && (
              <>
                <section id="analytics-section-overview" className="analytics-section">
                  <AnalyticsSectionHead title="Overview" helpKey="section-overview">
                    <ViewMoreBtn onClick={() => openDetail("kpis")} label="All metrics" />
                  </AnalyticsSectionHead>
                  <div className="analytics-kpi-grid">
                    {KPI_DEFS.map(({ key, label, helpKey, format: formatKpi = formatNumber }) => {
                      const kpi = data.kpis?.[key] || {};
                      return (
                        <div
                          key={key}
                          className="analytics-kpi-card analytics-kpi-card--clickable"
                          onClick={() => openDetail("kpis")}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") openDetail("kpis");
                          }}
                        >
                          <div className="analytics-kpi-card__top">
                            <span className="analytics-kpi-card__label">
                              {label}
                              {helpKey ? <AnalyticsHelpIcon helpKey={helpKey} /> : null}
                            </span>
                            <div
                              className="analytics-kpi-card__actions"
                              onClick={(e) => e.stopPropagation()}
                              onKeyDown={(e) => e.stopPropagation()}
                            >
                              <WidgetExportButton
                                sheet={exportSheets[`kpi-${key}`]}
                                slug={`kpi-${key}`}
                                widgetKey={`kpi-${key}`}
                                exportParams={params}
                                className="analytics-export-btn--kpi"
                              />
                              {compareEnabled && kpi.change_pct != null && <ChangeBadge pct={kpi.change_pct} />}
                            </div>
                          </div>
                          <div className="analytics-kpi-card__value">{formatKpi(kpi.value)}</div>
                          {compareEnabled && compLabel && <div className="analytics-kpi-card__vs">{compLabel}</div>}
                          {kpi.sparkline?.length > 1 && (
                            <div className="analytics-kpi-card__spark">
                              <HighchartsReact
                                highcharts={Highcharts}
                                options={sparklineOptions(kpi.sparkline)}
                                containerProps={{ style: { height: 32, width: "100%" } }}
                              />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  <div className="analytics-grid analytics-grid--4">
                    <Panel
                      title="Active Users Trend"
                      className="span-2"
                      detailKey="active-users"
                      exportSheet={exportSheets["active-users"]}
                      exportSlug="active-users"
                      showExport
                      exportParams={params}
                      onViewDetail={openDetail}
                      extra={
                        <div className="analytics-toggle">
                          <button type="button" className={trendMode === "daily" ? "active" : ""} onClick={() => setTrendMode("daily")}>Daily</button>
                          <button type="button" className={trendMode === "weekly" ? "active" : ""} onClick={() => setTrendMode("weekly")}>Weekly</button>
                        </div>
                      }
                    >
                      {hasActiveTrend ? (
                        <AnalyticsChart options={activeTrendChart} height={260} />
                      ) : (
                        <p className="analytics-empty">No active-user trend for this period / filter</p>
                      )}
                    </Panel>
                    <Panel
                      title="Top Acquisition Sources"
                      subtitle={`${formatNumber(acquisitionTotalUsers)} users`}
                      detailKey="acquisition-sources"
                      exportSheet={exportSheets["acquisition-sources"]}
                      exportSlug="acquisition-sources"
                      showExport
                      exportParams={params}
                      onViewDetail={openDetail}
                    >
                      {normalizedSources.length ? (
                        <>
                          <AnalyticsChart options={sourcesDonut} height={220} slotClassName="analytics-chart-slot--donut" />
                          <div className="analytics-platform-legend">
                            {normalizedSources.map((p) => (
                              <span key={p.label}>
                                {p.label}: {formatNumber(p.users)} ({formatPct(p.pct || 0)})
                              </span>
                            ))}
                          </div>
                        </>
                      ) : (
                        <p className="analytics-empty">No acquisition source data for this period / filter</p>
                      )}
                    </Panel>
                    <Panel
                      title="Platform Breakdown"
                      detailKey="platform"
                      exportSheet={exportSheets.platform}
                      exportSlug="platform"
                      showExport
                      exportParams={params}
                      onViewDetail={openDetail}
                    >
                      {normalizedPlatforms.length ? (
                        <>
                          <AnalyticsChart options={platformDonut} height={220} slotClassName="analytics-chart-slot--donut" />
                          <div className="analytics-platform-legend">
                            {normalizedPlatforms.map((p) => (
                              <span key={p.label}>
                                {p.label}: {formatNumber(p.users)} ({formatPct(p.pct || 0)})
                              </span>
                            ))}
                          </div>
                        </>
                      ) : (
                        <p className="analytics-empty">No platform breakdown for this period / filter</p>
                      )}
                    </Panel>
                  </div>

                  <div className="analytics-grid analytics-grid--2">
                    <Panel
                      title="Install Trend"
                      className="span-2"
                      subtitle={installTrendNote || undefined}
                      detailKey="installs-trend"
                      exportSheet={exportSheets["installs-trend"]}
                      exportSlug="installs-trend"
                      showExport
                      exportParams={params}
                      onViewDetail={openDetail}
                      extra={
                        <div className="analytics-toggle">
                          <button type="button" className={trendMode === "daily" ? "active" : ""} onClick={() => setTrendMode("daily")}>Daily</button>
                          <button type="button" className={trendMode === "weekly" ? "active" : ""} onClick={() => setTrendMode("weekly")}>Weekly</button>
                        </div>
                      }
                    >
                      {hasInstallTrend ? (
                        <AnalyticsChart options={installTrendChart} height={240} />
                      ) : (
                        <p className="analytics-empty">No install events for this period / filter</p>
                      )}
                    </Panel>
                  </div>
                </section>

                <section id="analytics-section-growth" className="analytics-section">
                  <AnalyticsSectionHead title="Growth" helpKey="section-growth">
                    <ViewMoreBtn onClick={() => openDetail("signup-funnel")} label="Full signup report" />
                  </AnalyticsSectionHead>
                  <div className="analytics-grid analytics-grid--2">
                    <Panel
                      title="Attribution Summary"
                      detailKey="attribution"
                      exportSheet={exportSheets.attribution}
                      exportSlug="attribution"
                      showExport
                      exportParams={params}
                      onViewDetail={openDetail}
                    >
                      <Table size="small" pagination={false} columns={attrColumns} dataSource={(data.acquisition?.summary || []).map((r, i) => ({ ...r, key: i }))} className="analytics-table" />
                    </Panel>
                    <Panel
                      title="Auth & Registration Performance"
                      detailKey="auth"
                      exportSheet={exportSheets.auth}
                      exportSlug="auth"
                      showExport
                      exportParams={params}
                      onViewDetail={openDetail}
                    >
                      <Table size="small" pagination={false} columns={authColumns} dataSource={(data.auth_performance_table || []).map((r) => ({ ...r, key: r.key }))} className="analytics-table" />
                    </Panel>
                  </div>
                  <div className="analytics-metric-strip analytics-metric-strip--signup">
                    <div>
                      <span>Flow started</span>
                      <strong>{formatNumber(signupSummary.flow_started)}</strong>
                      <small>{formatNumber(signupSummary.flow_started_unique)} unique</small>
                    </div>
                    <div>
                      <span>Completed</span>
                      <strong>{formatNumber(signupSummary.completed)}</strong>
                      <small>{formatPct(signupSummary.completion_rate || 0)} conversion</small>
                    </div>
                    <div>
                      <span>Abandoned</span>
                      <strong>{formatNumber(signupSummary.abandoned)}</strong>
                      <small>{formatPct(signupSummary.abandon_rate || 0)} of starts</small>
                    </div>
                    <div>
                      <span>Failed</span>
                      <strong>{formatNumber(signupSummary.failed)}</strong>
                      <small>validation / API errors</small>
                    </div>
                    <div>
                      <span>Drop-off (no register)</span>
                      <strong>{formatNumber(signupSummary.drop_off_without_registration)}</strong>
                      <small>started without signup_completed</small>
                    </div>
                  </div>
                  <div className="analytics-grid analytics-grid--2">
                    <Panel
                      title="Signup Funnel"
                      subtitle={`Started → Password → OTP → Completed · ${formatPct(data.signup_funnel?.overall_conversion_rate || 0)} overall`}
                      detailKey="signup-funnel"
                      exportSheet={exportSheets["signup-funnel"]}
                      exportSlug="signup-funnel"
                      showExport
                      exportParams={params}
                      onViewDetail={openDetail}
                    >
                      {signupFunnelSteps.length ? (
                        <>
                          <p className="analytics-chart-note">
                            {data?.signup_funnel?.funnel?.length
                              ? "PostHog funnel"
                              : "Built from signup step views (PostHog funnel API returned no rows)"}
                          </p>
                          <Table
                            size="small"
                            pagination={false}
                            columns={funnelColumns}
                            dataSource={signupFunnelSteps.map((r, i) => ({ ...r, key: i }))}
                            className="analytics-table"
                          />
                          <div className="analytics-funnel-chart">
                            <AnalyticsChart options={funnelChartOptions(signupFunnelSteps, { light: true })} height={280} />
                          </div>
                        </>
                      ) : (
                        <p className="analytics-empty">No signup funnel data for this period</p>
                      )}
                    </Panel>
                    <Panel
                      title="Step Completions"
                      subtitle="signup_step_completed by step"
                      detailKey="signup-funnel"
                      helpKey="step-completions"
                      exportSheet={exportSheets["step-completions"]}
                      exportSlug="step-completions"
                      showExport
                      exportParams={params}
                      onViewDetail={openDetail}
                    >
                      {signupStepCompletions.length ? (
                        <>
                          <AnalyticsChart options={signupCompletionBar} height={200} slotClassName="analytics-chart-slot--compact" />
                          <Table
                            size="small"
                            pagination={false}
                            columns={signupStepColumns}
                            dataSource={signupStepCompletions.map((r, i) => ({ ...r, key: i }))}
                            className="analytics-table analytics-detail-table-top"
                          />
                        </>
                      ) : (
                        <p className="analytics-empty">No step completion events yet</p>
                      )}
                    </Panel>
                  </div>
                </section>

                <section id="analytics-section-usage" className="analytics-section">
                  <AnalyticsSectionHead title="Usage & Adoption" helpKey="section-usage">
                    <ViewMoreBtn onClick={() => openDetail("drop-offs")} label="Drop-offs" />
                  </AnalyticsSectionHead>
                  <div className="analytics-metric-strip">
                    {ENGAGEMENT_DEFS.map(({ key, label, format, sub }) => {
                      const k = engKpis[key] || {};
                      const val = k.value ?? data.engagement?.[key];
                      return (
                        <div key={key}>
                          <span>{label}{sub ? ` (${sub})` : ""}</span>
                          <strong>{format ? format(val) : formatNumber(val)}</strong>
                          {compareEnabled && k.change_pct != null && <ChangeBadge pct={k.change_pct} invert={key.includes("bounce") || key.includes("failure")} />}
                        </div>
                      );
                    })}
                  </div>
                  <Panel
                    title="Attributed Installs by Channel"
                    detailKey="attributed-installs"
                    exportSheet={exportSheets["attributed-installs"]}
                    exportSlug="attributed-installs"
                    showExport
                    exportParams={params}
                    onViewDetail={openDetail}
                  >
                    {(data.attributed_installs || []).length ? (
                      <AnalyticsChart options={installsBar} height={200} slotClassName="analytics-chart-slot--compact" />
                    ) : (
                      <p className="analytics-empty">No attributed installs in this period</p>
                    )}
                  </Panel>
                  <div className="analytics-grid analytics-grid--2">
                    <Panel
                      title="Investment Funnel Overview"
                      subtitle={`Overall conversion: ${formatPct(data.investment_funnel?.overall_conversion_rate || 0)}`}
                      detailKey="investment-funnel"
                      exportSheet={exportSheets["investment-funnel"]}
                      exportSlug="investment-funnel"
                      showExport
                      exportParams={params}
                      onViewDetail={openDetail}
                    >
                      {funnelSteps.length ? (
                        <>
                          <p className="analytics-chart-note">
                            {data?.investment_funnel?.funnel?.length
                              ? "PostHog funnel"
                              : "Built from investment step events (PostHog funnel API returned no rows)"}
                          </p>
                          <Table size="small" pagination={false} columns={funnelColumns} dataSource={funnelSteps.map((r, i) => ({ ...r, key: i }))} className="analytics-table" />
                          <div className="analytics-funnel-chart">
                            <AnalyticsChart options={funnelChartOptions(funnelSteps, { light: true })} height={280} />
                          </div>
                        </>
                      ) : (
                        <p className="analytics-empty">No investment funnel data for this period. Users may not have reached investment steps yet.</p>
                      )}
                    </Panel>
                    <Panel
                      title="Top Screens (by Views)"
                      detailKey="top-screens"
                      exportSheet={exportSheets["top-screens"]}
                      exportSlug="top-screens"
                      showExport
                      exportParams={params}
                      onViewDetail={openDetail}
                    >
                      <Table size="small" pagination={false} columns={screenColumns} dataSource={(data.top_screens || []).slice(0, 8).map((r, i) => ({ ...r, key: i }))} className="analytics-table" />
                    </Panel>
                  </div>
                  <Panel
                    title="Lead to Investor Funnel"
                    subtitle="Page view → product → CTA → investment started → submitted"
                    detailKey="engagement"
                    helpKey="lead-to-investor"
                    exportSheet={exportSheets.engagement}
                    exportSlug="engagement"
                    showExport
                    exportParams={params}
                    onViewDetail={openDetail}
                    className="analytics-panel--lead-funnel"
                  >
                    {leadToInvestorSteps.length ? (
                      <>
                        <Table
                          size="small"
                          pagination={false}
                          columns={funnelColumns}
                          dataSource={leadToInvestorSteps.map((r, i) => ({ ...r, key: i }))}
                          className="analytics-table"
                        />
                        <div className="analytics-funnel-chart">
                          <AnalyticsChart options={leadToInvestorChart} height={280} />
                        </div>
                      </>
                    ) : (
                      <p className="analytics-empty">No lead-to-investor funnel data for this period</p>
                    )}
                  </Panel>
                  <Panel
                    title="Retention Cohorts (Weekly)"
                    detailKey="retention"
                    exportSheet={exportSheets.retention}
                    exportSlug="retention"
                    showExport
                    exportParams={params}
                    onViewDetail={openDetail}
                  >
                    {(data.retention?.cohorts || []).length ? (
                      <AnalyticsChart
                        options={retentionHeatmapOptions(data.retention?.cohorts || [], "Week")}
                        height={Math.max(260, (data.retention?.cohorts || []).length * 32 + 60)}
                      />
                    ) : (
                      <p className="analytics-empty">Not enough cohort history for a retention heatmap in this period</p>
                    )}
                  </Panel>
                </section>

                <section id="analytics-section-marketing" className="analytics-section">
                  <AnalyticsSectionHead title="Marketing & Operations" helpKey="section-marketing" />
                  <Panel
                    title="Hot leads — tapped invest, never finished"
                    subtitle="Updated on refresh · matched CRM contacts only"
                    detailKey="hot-leads"
                    exportSheet={exportSheets["hot-leads"]}
                    exportSlug="hot-leads"
                    hotLeadsRows={hotLeads.rows ?? []}
                    onViewDetail={openDetail}
                    className="analytics-panel--hot-leads"
                    extra={
                      <span className="analytics-hot-leads__badge">Call list · {hotLeads.total ?? hotLeads.rows?.length ?? 0}</span>
                    }
                  >
                    <HotLeadsPanel hotLeads={hotLeads} limit={8} showHeader={false} />
                  </Panel>
                  <div className="analytics-grid analytics-grid--2">
                    <Panel
                      title="Top Performing Products / Funds"
                      detailKey="products"
                      exportSheet={exportSheets.products}
                      exportSlug="products"
                      showExport
                      exportParams={params}
                      onViewDetail={openDetail}
                    >
                      {topProducts.length ? (
                        <Table
                          size="small"
                          pagination={false}
                          columns={productColumns}
                          dataSource={topProducts.map((r, i) => ({ ...r, key: i }))}
                          className="analytics-table"
                        />
                      ) : (
                        <p className="analytics-empty">No product view events in this period</p>
                      )}
                    </Panel>
                    <Panel
                      title="Campaign / Source ROI"
                      detailKey="campaign-roi"
                      exportSheet={exportSheets["campaign-roi"]}
                      exportSlug="campaign-roi"
                      showExport
                      exportParams={params}
                      onViewDetail={openDetail}
                    >
                      {campaignRoi.rows?.length ? (
                        <Table
                          size="small"
                          pagination={false}
                          columns={roiColumns}
                          dataSource={campaignRoi.rows.map((r, i) => ({ ...r, key: i }))}
                          className="analytics-table"
                        />
                      ) : (
                        <p className="analytics-empty">
                          {campaignRoi.warning ||
                            "No attributed campaign ROI data. Run Resync mappings after AppsFlyer/ad identity setup."}
                        </p>
                      )}
                    </Panel>
                  </div>
                  <div className="analytics-grid analytics-grid--2">
                    <Panel
                      title="Re-engagement Opportunities"
                      detailKey="re-engagement"
                      exportSheet={exportSheets["re-engagement"]}
                      exportSlug="re-engagement"
                      showExport
                      exportParams={params}
                      onViewDetail={openDetail}
                    >
                      {reengagement.length ? (
                        <Table
                          size="small"
                          pagination={false}
                          columns={reengagementColumns}
                          dataSource={reengagement.map((r, i) => ({ ...r, key: i }))}
                          className="analytics-table"
                        />
                      ) : (
                        <p className="analytics-empty">No re-engagement cohorts detected for this period</p>
                      )}
                    </Panel>
                    <Panel
                      title="Events Overview"
                      subtitle={`${data.event_stats?.types_count ?? 0} event types · ${formatNumber(data.event_stats?.total_count)} total`}
                      detailKey="events"
                      exportSheet={exportSheets.events}
                      exportSlug="events"
                      showExport
                      exportParams={params}
                      onViewDetail={openDetail}
                    >
                      {eventsOverview.length ? (
                        <Table size="small" pagination={false} columns={eventColumns} dataSource={eventsOverview.slice(0, 8).map((r, i) => ({ ...r, key: i }))} className="analytics-table" />
                      ) : (
                        <p className="analytics-empty">No categorized events for this period / filter</p>
                      )}
                    </Panel>
                  </div>
                  <div className="analytics-grid analytics-grid--2">
                    <Panel
                      title="Latest Activity"
                      subtitle="Most recent events in the selected date range"
                      detailKey="realtime"
                      helpKey="latest-activity"
                      exportSheet={exportSheets.realtime}
                      exportSlug="realtime"
                      showExport
                      exportParams={params}
                      onViewDetail={openDetail}
                      extra={
                        <Tooltip title="Events from the selected date range (not a live websocket feed)">
                          <span className="analytics-live-badge analytics-live-badge--pulse">Live</span>
                        </Tooltip>
                      }
                    >
                      <ul className="analytics-realtime-feed">
                        {(data.realtime_activity || []).slice(0, 8).map((ev, i) => (
                          <li key={`${ev.timestamp}-${i}`}>
                            <div>
                              <strong>{ev.label || ev.event}</strong>
                              <span>{dayjs(ev.timestamp).format("HH:mm:ss")}</span>
                            </div>
                            <span className={`platform-tag platform-tag--${ev.platform}`}>{formatPlatformLabel(ev.platform)}</span>
                          </li>
                        ))}
                        {!data.realtime_activity?.length && (
                          <li className="analytics-empty">No events in the selected date range</li>
                        )}
                      </ul>
                    </Panel>
                    <Panel
                      title="Geographic Insights"
                      helpKey="geographic-insights"
                      subtitle={
                        geoSummary.totals?.countries
                          ? `${formatNumber(geoSummary.totals.countries)} countries · ${formatNumber(geoSummary.totals.users)} users`
                          : "User locations from page_view geo properties"
                      }
                      exportSheet={exportSheets["geo-countries"]}
                      exportSlug="geo-countries"
                      showExport
                      exportParams={params}
                      extra={
                        <Link to="/analytics-usage/map" className="analytics-view-more">
                          View full map <i className="fa-solid fa-arrow-right" />
                        </Link>
                      }
                    >
                      {(geoSummary.user_markers || []).length ? (
                        <div className="analytics-geo-embed">
                          <WorldUserMap markers={geoSummary.user_markers} />
                        </div>
                      ) : (
                        <p className="analytics-empty">No geo coordinates in this period</p>
                      )}
                      {(geoSummary.countries || []).length ? (
                        <Table
                          size="small"
                          pagination={false}
                          className="analytics-table analytics-detail-table-top"
                          columns={geoCountryColumns}
                          dataSource={(geoSummary.countries || []).slice(0, 5).map((r, i) => ({ ...r, key: i }))}
                        />
                      ) : null}
                    </Panel>
                  </div>
                </section>

                <section id="analytics-section-errors" className="analytics-section">
                  <AnalyticsSectionHead title="Errors & Data Quality" helpKey="section-errors" />
                  <div className="analytics-grid analytics-grid--3">
                    <Panel
                      title="Alerts & Insights"
                      detailKey="alerts"
                      exportSheet={exportSheets.alerts}
                      exportSlug="alerts"
                      showExport
                      exportParams={params}
                      onViewDetail={openDetail}
                    >
                      <ul className="analytics-alerts">
                        {(data.alerts || []).slice(0, 4).map((a, i) => (
                          <li key={i} className={`analytics-alert analytics-alert--${a.severity}`}>
                            <i className={`fa-solid fa-${a.severity === "error" ? "circle-xmark" : a.severity === "warning" ? "triangle-exclamation" : "circle-check"}`} />
                            <div><strong>{a.title}</strong><p>{a.message}</p></div>
                          </li>
                        ))}
                      </ul>
                    </Panel>
                    <Panel
                      title="Top Errors"
                      detailKey="errors"
                      exportSheet={exportSheets.errors}
                      exportSlug="errors"
                      showExport
                      exportParams={params}
                      onViewDetail={openDetail}
                    >
                      <Table size="small" pagination={false} columns={errorColumns} dataSource={(data.errors || []).slice(0, 5).map((r, i) => ({ ...r, key: i }))} className="analytics-table" locale={{ emptyText: "No errors" }} />
                    </Panel>
                    <Panel
                      title="Data Quality"
                      detailKey="data-quality"
                      exportSheet={exportSheets["data-quality"]}
                      exportSlug="data-quality"
                      showExport
                      exportParams={params}
                      onViewDetail={openDetail}
                    >
                      <AnalyticsChart
                        options={dataQualityDonut({
                          good: dq.good_events,
                          dropped: dq.dropped,
                          duplicate: dq.duplicate,
                          goodPct: dq.good_pct,
                        })}
                        height={220}
                        slotClassName="analytics-chart-slot--donut"
                      />
                      <div className="analytics-dq-legend">
                        <span><i className="dot dot--ok" /> Good: {formatNumber(dq.good_events)}</span>
                        <span><i className="dot dot--err" /> Dropped: {formatNumber(dq.dropped)}</span>
                        <span><i className="dot dot--warn" /> Duplicate: {formatNumber(dq.duplicate)}</span>
                      </div>
                    </Panel>
                  </div>
                </section>
              </>
            )}

            <AnalyticsDetailModal
              open={Boolean(detailKey)}
              detailKey={detailKey}
              onClose={closeDetail}
              data={data}
              params={params}
              compareEnabled={compareEnabled}
              compLabel={compLabel}
              exportSheets={exportSheets}
            />
          </main>
    </div>
    </AnalyticsExportContext.Provider>
  );
};

export default AnalyticsUsageDashboard;
